// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  auditedMessageTypeSchema,
  toAuditedMessageTypeLabel,
} from '@/constants/audited-message-type';
import {
  messageChannelSchema,
  toMessageChannelLabel,
} from '@/notification/api/notification-types';
import { useMessageTypes } from '@/notification/hooks/use-notification-subscriptions';
import { Skeleton, Stack, Typography } from '@mui/material';
import { uniq, without } from 'lodash-es';

// Matches one `MESSAGE_TYPE (CHANNEL, CHANNEL)` entry of the stored summary
const SUMMARY_ENTRY_PATTERN = /([A-Z0-9_]+) \(([^)]*)\)/g;

const CHANNEL_SEPARATOR = ', ';

// The stored name says in-app, but the list covers every channel
const HEADING = 'Message types';

const LOADING_LINE_WIDTH_PX = 240;

const LIST_FORMATTER = new Intl.ListFormat(undefined, {
  type: 'conjunction',
  style: 'long',
});

// A digest topic retired with the email simplification is not in the catalog
function toRetiredMessageTypeLabel(messageType: string): string | undefined {
  const result = auditedMessageTypeSchema.safeParse(messageType);
  return result.success ? toAuditedMessageTypeLabel(result.data) : undefined;
}

interface SubscriptionChange {
  messageTypeLabel: string;
  subscribedChannelLabels: string[];
  unsubscribedChannelLabels: string[];
}

interface Props {
  oldValue: string | null;
  newValue: string | null;
}

/** Renders each message type whose subscribed channels a change moved. */
export function AuditInAppMessageTypesLabel(props: Props) {
  const messageTypes = useMessageTypes();
  if (messageTypes.isPending) {
    return (
      <Stack>
        <Typography component="span" variant="caption" fontWeight="bold">
          {HEADING}:
        </Typography>
        <Skeleton
          aria-label="Loading message types"
          variant="text"
          width={LOADING_LINE_WIDTH_PX}
          sx={{ marginLeft: 2 }}
        />
      </Stack>
    );
  }

  const messageTypeLabels = new Map(
    (messageTypes.data ?? []).map((entry) => [entry.messageType, entry.label])
  );

  // The summary was stored as enum text, so each entry is translated here,
  // and an entry naming a type or channel we cannot label is left out.
  const toChannelLabelsByMessageType = (
    summary: string | null
  ): Map<string, string[]> => {
    const channelLabelsByMessageType = new Map<string, string[]>();
    for (const match of summary?.matchAll(SUMMARY_ENTRY_PATTERN) ?? []) {
      const messageTypeLabel =
        messageTypeLabels.get(match[1]) ?? toRetiredMessageTypeLabel(match[1]);
      const channelLabels = match[2]
        .split(CHANNEL_SEPARATOR)
        .flatMap((channel) => {
          const result = messageChannelSchema.safeParse(channel);
          return result.success ? [toMessageChannelLabel(result.data)] : [];
        });
      if (messageTypeLabel !== undefined && channelLabels.length > 0) {
        channelLabelsByMessageType.set(messageTypeLabel, channelLabels);
      }
    }

    return channelLabelsByMessageType;
  };

  const oldChannelLabels = toChannelLabelsByMessageType(props.oldValue);
  const newChannelLabels = toChannelLabelsByMessageType(props.newValue);

  // A message type whose channels came out the same on both sides did not change.
  const changes = uniq([
    ...oldChannelLabels.keys(),
    ...newChannelLabels.keys(),
  ]).flatMap((messageTypeLabel): SubscriptionChange[] => {
    const oldLabels = oldChannelLabels.get(messageTypeLabel) ?? [];
    const newLabels = newChannelLabels.get(messageTypeLabel) ?? [];
    const subscribedChannelLabels = without(newLabels, ...oldLabels);
    const unsubscribedChannelLabels = without(oldLabels, ...newLabels);
    if (
      subscribedChannelLabels.length === 0 &&
      unsubscribedChannelLabels.length === 0
    ) {
      return [];
    }

    return [
      { messageTypeLabel, subscribedChannelLabels, unsubscribedChannelLabels },
    ];
  });
  if (changes.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {HEADING}:
      </Typography>
      <Stack role="list" sx={{ marginLeft: 2 }}>
        {changes.map((change) => (
          <Typography
            key={change.messageTypeLabel}
            role="listitem"
            variant="caption"
          >
            <b>{change.messageTypeLabel}</b>:{' '}
            {change.subscribedChannelLabels.length > 0 && (
              <>
                subscribed via{' '}
                <b>{LIST_FORMATTER.format(change.subscribedChannelLabels)}</b>
              </>
            )}
            {change.subscribedChannelLabels.length > 0 &&
              change.unsubscribedChannelLabels.length > 0 &&
              '; '}
            {change.unsubscribedChannelLabels.length > 0 && (
              <>
                unsubscribed via{' '}
                <b>{LIST_FORMATTER.format(change.unsubscribedChannelLabels)}</b>
              </>
            )}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}
